import { log } from './LoggingService';
import { performanceMonitor } from './PerformanceMonitoringService';
import { dataSourceManager, DataSourceType } from './DataSourceManager';
import { dataValidationService } from './DataValidationService';
/**
 * V2 Data Service for Single Pain of Glass Dashboard
 * Optimized for sub-second chart data loading
 */

export interface V2ChartData {
  chart_type: 'grouped_bar';
  title: string;
  subtitle: string;
  x_axis: string[];
  series: {
    name: string;
    color: string;
    data: number[];
    deal_counts?: number[]; // Optional deal counts for funding charts
  }[];
  totals: {
    current: number;
    previous: number;
  };
  summary?: {
    // Funding charts
    current_amount?: number;
    current_deals?: number;
    previous_amount?: number;
    previous_deals?: number;
    // M&A charts
    current_count?: number;
    previous_count?: number;
    // Common
    growth_percent?: number;
    period_label?: string;
  };
}

export interface V2Response {
  charts: {
    funding_trends: V2ChartData;
    ma_activity: V2ChartData;
    country_funding?: any; // Country funding data from the API
  };
  metadata: {
    timeframe: string;
    week_offset: number;
    current_period: {
      start: string;
      end: string;
    };
    previous_period: {
      start: string;
      end: string;
    };
    navigation: {
      can_navigate_back: boolean;
      can_navigate_forward: boolean;
      current_offset: number;
      next_offset: number;
      prev_offset: number;
    };
    generated_at: string;
    total_records: number;
  };
  cache_status: 'HIT' | 'MISS';
  response_time_ms: number;
  performance_target_met: boolean;
}

export interface V2CacheHealth {
  cache_namespace: string;
  total_timeframes: number;
  cached_timeframes: number;
  health_checks: {
    timeframe: string;
    cache_key: string;
    cached: boolean;
    age_ms: number | null;
  }[];
  timestamp: string;
}

export class V2DataService {
  public baseUrl: string;
  private cache: Map<string, { data: V2Response; timestamp: number }> = new Map();
  private cacheTimeout = 5 * 60 * 1000; // 5 minutes local cache

  constructor() {
    // Use environment-specific URLs
    this.baseUrl = this.getWorkerUrl();
  }

  private getWorkerUrl(): string {
    // Use environment variable for API endpoint
    const url = import.meta.env.VITE_API_ENDPOINT || 'https://spog-aggregation.mike-532.workers.dev';
    const isDevelopment = import.meta.env.DEV;

    // Only log in development to avoid production console spam
    if (isDevelopment) {
      console.log('🔧 V2DataService using URL:', url);
    }
    return url;
  }

  /**
   * Get chart data for both Funding Trends and M&A Activity
   * @param timeframe - Time period to fetch (e.g., '12w', 'ytd', 't12m')
   * @param weekOffset - Week navigation offset
   * @param view - View type (default '3year')
   * @param filters - Optional filter parameters (regions, countries, etc.)
   */
  async getChartData(
    timeframe: string = '12w',
    weekOffset: number = 0,
    view: string = '3year',
    filters?: Record<string, string[] | undefined>
  ): Promise<V2Response> {
    // Build filter query string
    const filterParams = filters
      ? Object.entries(filters)
          .filter(([, values]) => values && values.length > 0)
          .map(([key, values]) => `${key}=${values!.join(',')}`)
          .join('&')
      : '';

    const cacheKey = `${view}-${timeframe}-${weekOffset}${filterParams ? `-${filterParams}` : ''}`;
    const cached = this.cache.get(cacheKey);

    // Check local cache first (for instant navigation)
    if (cached && (Date.now() - cached.timestamp) < this.cacheTimeout) {
      log.debug('V2 Service: Local cache hit', "Debug");
      performanceMonitor.trackCacheAccess('local_cache', true, 0);
      return {
        ...cached.data,
        cache_status: 'HIT'
      };
    }

    const startTime = Date.now();

    try {
      const baseEndpoint = `/api/v2/charts?view=${view}&metrics=weekly&timeframe=${timeframe}&week_offset=${weekOffset}`;
      const endpoint = filterParams ? `${baseEndpoint}&${filterParams}` : baseEndpoint;
      log.debug(`V2 Service: Fetching ${endpoint}`, "Debug");

      // Use DataSourceManager for intelligent data fetching with fallback
      const result = await dataSourceManager.getData<V2Response>(endpoint, {}, {
        forceRefresh: !this.cache.has(cacheKey),
        preferredSource: DataSourceType.CACHE, // Try cache first
        timeout: 10000 // 10 second timeout
      });

      if (result.status === 'error') {
        throw new Error(result.metadata?.errorMessage || 'Failed to fetch data');
      }

      const data: V2Response = result.data;
      const responseTime = Date.now() - startTime;

      // Validate the response data
      const validation = dataValidationService.validateApiResponse(data);
      if (!validation.isValid) {
        log.warn(`V2 Service: Data validation failed: ${validation.errors.join(', ')}`, "DataValidation");
      }
      if (validation.warnings.length > 0) {
        log.warn(`V2 Service: Data validation warnings: ${validation.warnings.join(', ')}`, "DataValidation");
      }

      log.debug(`V2 Service: Response received in ${responseTime}ms from ${result.source} (target: <2000ms)`, "Debug");

      // Track performance metrics
      performanceMonitor.trackApiCall(
        '/api/v2/charts',
        'GET',
        responseTime,
        200, // Assume success since we got data
        { timeframe, weekOffset, view, dataSize: JSON.stringify(data).length }
      );
      
      performanceMonitor.trackQuery(
        'getChartData',
        responseTime,
        data.metadata?.total_records || 0,
        { timeframe, weekOffset, view }
      );

      // Update local cache
      this.cache.set(cacheKey, {
        data: {
          ...data,
          response_time_ms: responseTime
        },
        timestamp: Date.now()
      });

      return {
        ...data,
        response_time_ms: responseTime,
        performance_target_met: responseTime < 2000
      };

    } catch (error) {
      console.error('V2 Service: Failed to fetch chart data:', error);
      throw new Error(`Failed to load chart data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Warm the cache for all timeframes
   */
  async warmCache(): Promise<any> {
    try {
      const url = `${this.baseUrl}/api/v2/cache/warm`;
      log.debug('V2 Service: Warming cache...', "Debug");

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      log.debug('V2 Service: Cache warm completed:', JSON.stringify(result), "Debug");

      return result;

    } catch (error) {
      console.error('V2 Service: Cache warm failed:', error);
      throw error;
    }
  }

  /**
   * Get cache health status
   */
  async getCacheHealth(): Promise<V2CacheHealth> {
    try {
      const url = `${this.baseUrl}/api/v2/cache/health`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();

    } catch (error) {
      console.error('V2 Service: Failed to get cache health:', error);
      throw error;
    }
  }

  /**
   * Clear local cache
   */
  clearLocalCache(): void {
    this.cache.clear();
    log.debug('V2 Service: Local cache cleared', "Debug");
  }

  /**
   * Preload data for common timeframes to improve UX
   */
  async preloadTimeframes(): Promise<void> {
    const commonTimeframes = ['1q', '6m', 'ytd'];

    try {
      const preloadPromises = commonTimeframes.map(timeframe =>
        this.getChartData(timeframe, 0).catch(error => {
          console.warn(`V2 Service: Failed to preload ${timeframe}:`, error);
          return null;
        })
      );

      await Promise.all(preloadPromises);
      log.debug('V2 Service: Preload completed for common timeframes', "Debug");

    } catch (error) {
      console.warn('V2 Service: Preload failed:', error);
    }
  }

  /**
   * Get geo-funding data aggregated by country or other geographic fields
   * Fetches from real API endpoint with fallback to mock data
   */
  async getGeoFundingData(weekOffset: number = 0, timeframe: string = '12weeks', groupBy: string = 'company_country'): Promise<any> {
    try {
      const params = new URLSearchParams({
        timeframe,
        groupBy
      });

      const response = await fetch(`${this.baseUrl}/api/data/geo-funding?${params}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('Failed to fetch geo funding data:', response.status);
        // Return empty data structure on API failure
        return {
          week: `${timeframe} data`,
          countries: []
        };
      }

      const data = await response.json();
      log.debug('Geo funding API response:', JSON.stringify(data), "Debug");
      return data;
    } catch (error) {
      console.error('Error fetching geo funding data:', error);
      // Return empty data structure on error
      return {
        week: `${timeframe} data`,
        countries: []
      };
    }
  }

  /**
   * Helper to get ISO week number
   */
  private getISOWeek(date: Date): number {
    const target = new Date(date.valueOf());
    const dayNum = (date.getDay() + 6) % 7;
    target.setDate(target.getDate() - dayNum + 3);
    const firstThursday = target.valueOf();
    target.setMonth(0, 1);
    if (target.getDay() !== 4) {
      target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
    }
    return 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000);
  }

  /**
   * Navigate to different week offset
   */
  async navigateWeeks(timeframe: string, direction: 'forward' | 'backward', currentOffset: number): Promise<V2Response> {
    const newOffset = direction === 'backward' ? currentOffset + 1 : currentOffset - 1;

    // Ensure offset doesn't go negative or too far back
    const clampedOffset = Math.max(0, Math.min(52, newOffset));

    return this.getChartData(timeframe, clampedOffset);
  }

  /**
   * Get available timeframes
   */
  getTimeframes(): { key: string; label: string; default?: boolean }[] {
    return [
      { key: '1w', label: '1 Week' },
      { key: '2w', label: '2 Weeks' },
      { key: '4w', label: '4 Weeks' },
      { key: '1q', label: '1 Quarter', default: true },
      { key: '6m', label: '6 Months' },
      { key: '9m', label: '9 Months' },
      { key: '5q', label: '5 Quarters' },
      { key: 'ytd', label: 'Year to Date' },
      { key: 'ttm', label: 'Trailing 12 Months' }
    ];
  }

  /**
   * NEW: Get comparison chart data for country/region comparisons
   * @param comparisonType - Type of comparison (us-israel, us-europe, us-uk, us-eu)
   * @param timeframe - Time period to fetch (e.g., '12w', 'ytd', 't12m')
   * @param weekOffset - Week navigation offset
   * @param filters - Optional filter parameters
   */
  async getComparisonData(
    comparisonType: 'us-israel' | 'us-europe' | 'us-uk' | 'us-eu',
    timeframe: string = '12w',
    weekOffset: number = 0,
    filters?: Record<string, string[] | undefined>
  ): Promise<any> {
    // Build filter query string
    const filterParams = filters
      ? Object.entries(filters)
          .filter(([, values]) => values && values.length > 0)
          .map(([key, values]) => `${key}=${values!.join(',')}`)
          .join('&')
      : '';

    const cacheKey = `comparison-${comparisonType}-${timeframe}-${weekOffset}${filterParams ? `-${filterParams}` : ''}`;
    const cached = this.cache.get(cacheKey);

    // Check local cache first
    if (cached && (Date.now() - cached.timestamp) < this.cacheTimeout) {
      log.debug('V2 Service: Local cache hit for comparison', "Debug");
      performanceMonitor.trackCacheAccess('local_cache', true, 0);
      return {
        ...cached.data,
        cache_status: 'HIT'
      };
    }

    const startTime = Date.now();

    try {
      const baseEndpoint = `/api/v2/comparison/${comparisonType}?timeframe=${timeframe}&offset=${weekOffset}`;
      const endpoint = filterParams ? `${baseEndpoint}&${filterParams}` : baseEndpoint;
      log.debug(`V2 Service: Fetching comparison ${endpoint}`, "Debug");

      // Use DataSourceManager for intelligent data fetching with fallback
      const result = await dataSourceManager.getData<any>(endpoint, {}, {
        forceRefresh: !this.cache.has(cacheKey),
        preferredSource: DataSourceType.CACHE,
        timeout: 10000
      });

      if (result.status === 'error') {
        throw new Error(result.metadata?.errorMessage || 'Failed to fetch comparison data');
      }

      const data = result.data;
      const responseTime = Date.now() - startTime;

      log.debug(`V2 Service: Comparison response received in ${responseTime}ms from ${result.source}`, "Debug");

      // Track performance metrics
      performanceMonitor.trackApiCall(
        `/api/v2/comparison/${comparisonType}`,
        'GET',
        responseTime,
        200,
        { timeframe, weekOffset, comparisonType, dataSize: JSON.stringify(data).length }
      );

      performanceMonitor.trackQuery(
        'getComparisonData',
        responseTime,
        data.metadata?.total_records || 0,
        { timeframe, weekOffset, comparisonType }
      );

      // Update local cache
      this.cache.set(cacheKey, {
        data: {
          ...data,
          response_time_ms: responseTime
        },
        timestamp: Date.now()
      });

      return {
        ...data,
        response_time_ms: responseTime
      };

    } catch (error) {
      console.error('V2 Service: Failed to fetch comparison data:', error);
      throw new Error(`Failed to load comparison data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}